import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { UserRound, Store, ShieldCheck, Sparkles, Gift, Smartphone } from "lucide-react";
import Logo, { NouvelleLogo } from "@/components/Logo";
import StorePinGate from "@/components/StorePinGate";
import { AdminLoginForm } from "@/pages/LoginPage";
import { CustomerLoginForm } from "@/pages/CustomerLogin";
import { useAuth } from "@/lib/auth";
import { useCustomer } from "@/lib/customer";

const PERK_ANIM = { initial: { opacity: 0, x: -12 }, animate: { opacity: 1, x: 0 } };
const PILL_SPRING = { type: "spring", stiffness: 400, damping: 30 };
const PANEL_ANIM = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -10 }, transition: { duration: 0.18 } };

const TABS = [
  { id: "cliente", label: "Cliente", icon: UserRound, hint: "Gioca e ritira i tuoi premi" },
  { id: "sede", label: "Punto Vendita", icon: Store, hint: "Verifica e consegna premi" },
  { id: "admin", label: "Amministratore", icon: ShieldCheck, hint: "Gestione concorsi" },
];

const PERKS = [
  [Gift, "Gioca ai concorsi instant win", "Dal totem in negozio o dal tuo smartphone"],
  [Smartphone, "Premi sempre con te", "Ricevute digitali, promemoria via email e WhatsApp"],
  [Sparkles, "Un solo account", "Valido per tutti i concorsi Vivi Win"],
];

export default function UnifiedLogin() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { customer } = useCustomer();
  const next = params.get("next");
  const tab = TABS.some((t) => t.id === params.get("tab")) ? params.get("tab") : "cliente";
  const setTab = (id) => setParams((p) => { p.set("tab", id); return p; }, { replace: true });
  const [storeUnlocked, setStoreUnlocked] = useState(false);
  useEffect(() => { if (storeUnlocked) navigate("/verifica", { replace: true }); }, [storeUnlocked, navigate]);

  if (tab === "admin" && user) return <Navigate to="/" replace />;
  if (tab === "cliente" && customer) return <Navigate to={next || "/concorsi"} replace />;

  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-[1.1fr_1fr]" data-testid="unified-login">
      <aside className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-[#eef4fd] px-14 py-12 border-r border-border">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-[var(--nc-blue)]/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-[var(--nc-green)]/15 blur-3xl" />
        <div className="relative flex items-center gap-4"><NouvelleLogo className="h-24" /><div><p className="font-display font-black text-2xl text-[var(--nc-blue)] leading-tight">Nouvelle Contest</p><p className="text-sm text-muted-foreground">La piattaforma dei concorsi a premi</p></div></div>
        <div className="relative space-y-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary font-bold flex items-center gap-2"><Logo className="h-8" /> Vivi Win · Soluzione instant win</p>
            <h1 className="font-display font-black text-4xl xl:text-5xl leading-tight mt-3">Un solo accesso,<br />tutte le tue vincite.</h1>
            <p className="text-muted-foreground mt-4 max-w-md">Clienti, punti vendita e amministratori entrano da qui: scegli il tuo profilo e continua.</p>
          </div>
          <ul className="space-y-4">
            {PERKS.map(([Icon, t, d], i) => (
              <motion.li key={t} {...PERK_ANIM} transition={{ delay: 0.15 + i * 0.1 }} className="flex items-start gap-3">
                <span className="h-10 w-10 shrink-0 rounded-xl bg-card border border-border grid place-items-center"><Icon className="h-5 w-5 text-amber-400" /></span>
                <div><p className="font-semibold">{t}</p><p className="text-sm text-muted-foreground">{d}</p></div>
              </motion.li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-muted-foreground"><span className="font-bold text-foreground">Vivi Win</span> è una soluzione <span className="font-bold text-[var(--nc-blue)]">Nouvelle Contest</span> · © {new Date().getFullYear()}</p>
      </aside>

      <main className="flex flex-col justify-center px-5 py-10 sm:px-10 lg:px-16">
        <div className="lg:hidden flex items-center gap-3 mb-8"><NouvelleLogo className="h-14" /><div><p className="font-display font-black text-xl">Vivi Win</p><p className="text-xs text-muted-foreground">Soluzione Nouvelle Contest · Accedi al tuo profilo</p></div></div>
        <div className="w-full max-w-md mx-auto lg:mx-0">
          <div className="grid grid-cols-3 gap-1 rounded-2xl bg-card/70 border border-border p-1" role="tablist" data-testid="login-tabs">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`login-tab-${id}`}
                className={`relative flex flex-col items-center gap-1 rounded-xl px-2 py-3 text-xs sm:text-sm font-medium transition-colors ${tab === id ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {tab === id && <motion.span layoutId="login-tab-pill" className="absolute inset-0 rounded-xl bg-primary/15 border border-primary/40" transition={PILL_SPRING} />}
                <Icon className={`relative h-5 w-5 ${tab === id ? "text-primary" : ""}`} />
                <span className="relative">{label}</span>
              </button>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mt-3 mb-5" data-testid="login-tab-hint">{TABS.find((t) => t.id === tab).hint}</p>
          <AnimatePresence mode="wait">
            <motion.div key={tab} {...PANEL_ANIM}>
              {tab === "cliente" && <CustomerLoginForm next={next || "/concorsi"} />}
              {tab === "sede" && <StorePinGate onUnlock={() => setStoreUnlocked(true)} embedded />}
              {tab === "admin" && <AdminLoginForm />}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
